import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import {
  GridDataResult,
  SelectableSettings
} from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor, State } from '@progress/kendo-data-query';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ResulteableComponent } from '../../../../shared/components/interfaces/resulteable.component';
import { ColumnSettings } from '../../../../shared/model/column-settings.interface';
import { GridSettings } from '../../../../shared/model/grid-settings.interface';
import { DataTableUtils } from '../../../../shared/utils/data-table.utils';
import { AuthService } from '../../../security/service/auth.service';
import { ProductListResultColumnsModel } from '../../model/product-list-results-columns.model';
import { ProductRoutingStates } from '../../product-routing.module';
import { ProductService } from '../../service/product.service';

@Component({
  selector: 'product-list-results',
  templateUrl:
    './console/app-js/modules/product/components/product-list-results/product-list-results.component.html'
})
export class ProductListResultsComponent extends APIOperableComponent
  implements OnInit, OnChanges, ResulteableComponent {
  @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;
  @ViewChild('vixxoGrid', { static: true }) vixxoGrid: any;
  @Output() activateEvent = new EventEmitter<any>();
  @Input('data') data: any[];
  @Input('noResults') noResults: boolean;
  @Input('loadingIndicator') loadingIndicator: boolean;
  @Output() deleteEvent = new EventEmitter<any>();
  private datePipe: DatePipe;
  protected rowHeight: number;
  private readonly MACHINE_DETAIL_CARD_HEIGHT = 160;
  private readonly PART_DETAIL_CARD_HEIGHT = 390;
  public gridView: GridDataResult;
  public sort: SortDescriptor[] = [];
  protected listOfSelectedObjects = [];
  protected products = [];
  @Output('pageChange') pageChange = new EventEmitter<any>();
  columns = ProductListResultColumnsModel;
  noRecordsSettings = {
    message: '',
    styleClass: 'no-records-wrapper'
  };
  public selectableSettings: SelectableSettings = {
    enabled: true,
    checkboxOnly: true
  };

  /**
   * @constructor
   * @param {ProductService} productService
   */
  constructor(
    private productService: ProductService,
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private router: Router
  ) {
    super(progressService, toastr, authService);
    const gridSettings: GridSettings = this.productService.getStatePersistingInputs();
    if (gridSettings) {
      this.gridSettings = this.mapGridSettings(gridSettings);
    }
    this.loadProducts();

    this.datePipe = new DatePipe('en-US');
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.loadProducts();
  }

  private loadProducts(): void {
    if (this.data !== undefined && this.data.length !== 0) {
      this.products = JSON.parse(JSON.stringify(this.data));
      this.products.forEach(obj => {
        obj.make = `<a class="cellLink"><span>${
          obj.make ? obj.make : '(no make)'
        }</span></a>`;
        obj.model = `<a class="cellLink"><span>${
          obj.model ? obj.model : '(no model)'
        }</span></a>`;
        obj.requiresAudit = obj.requiresAudit === true ? 'Yes' : 'No';
      });
      this.gridView = {
        data: orderBy(this.products, this.sort),
        total: this.products.length
      };
    } else {
      this.products = [];
      if (this.noResults) {
        this.noRecordsSettings.message = 'No Results Found';
        this.noRecordsSettings.styleClass = null;
      }
    }
    // Set Kendo Grid Columns to user previous used.
    if (this.gridSettings.realColWidths.length > 0) {
      setTimeout(
        () =>
          this.vixxoGrid.setGridColumnWidths(this.gridSettings.realColWidths),
        300
      );
    }
  }

  /**
   * Implementation from OnInit interface
   */
  public ngOnInit(): void {
    this.datePipe = new DatePipe('en-US');
  }

  /**
   * Implementation from OnChanges interface
   * On every change we clean the current selection of values.
   */
  public ngOnChanges(): void {
    this.deleteEvent.next([]);
    this.listOfSelectedObjects = [];
    this.loadProducts();
  }

  /**
   * Expands the Detail of a Product
   * @param row
   */
  onExpandRow(row) {
    this.rowHeight =
      row.type === 'Part'
        ? this.PART_DETAIL_CARD_HEIGHT
        : this.MACHINE_DETAIL_CARD_HEIGHT;
    setTimeout(() => DataTableUtils.expandRow(this.table, row), 200);
  }

  /**
   * Collapses the Detail of a Product row.
   */
  onCollapseRow() {
    DataTableUtils.collapseRow(this.table);
  }

  /**
   * TODO: document this.
   * @param productId
   */
  onItemDetail(productId: any): void {
    this.router.navigate([
      `/console/${ProductRoutingStates.LIST.url}`,
      productId,
      'view'
    ]);
  }

  /**
   * Selects an item by clicking the checkbox from the list
   *
   * @param event
   */
  selectedRowChange(event) {
    let activeSelectedItems = [];

    this.searchForArray(this.listOfSelectedObjects, event.deselectedRows);

    for (let i = 0; i < event.selectedRows.length; i++) {
      this.listOfSelectedObjects.push(event.selectedRows[i]);
    }
    for (let i = 0; i < this.listOfSelectedObjects.length; i++) {
      if (!this.listOfSelectedObjects[i].dataItem.hasOwnProperty('endDate')) {
        activeSelectedItems.push(this.listOfSelectedObjects[i].dataItem);
      }
    }
    if (activeSelectedItems !== []) {
      this.deleteEvent.next(activeSelectedItems);
    }
  }

  searchForArray(selectedItems, deselectedItmes) {
    for (let i = 0; i < selectedItems.length; i++) {
      for (let j = 0; j < deselectedItmes.length; j++) {
        if (selectedItems[i].dataItem === deselectedItmes[j].dataItem) {
          selectedItems.splice(i, 1);
        }
      }
    }
  }

  /**
   * Get the entire Row class
   * @param row
   */
  protected getRowClass = (row: any): any => {
    return {
      'inactive-row': row.dataItem.endDate
        ? !this.productService.isProductActive(row.dataItem.endDate)
        : false
    };
  };

  /**
   * grid cell click event
   */
  public onCellClick(event) {
    if (event.column.cssClass.includes('link')) {
      this.onItemDetail(event.dataItem.id);
    }
  }

  /**
   * Infinity scrolling pagination functionality
   */
  infinityScroll(): void {
    this.pageChange.emit();
  }

  /**
   * Reset column widths and delete current custom widths.
   */
  public appliedColumnOrder(e: Array<ColumnSettings>) {
    this.gridSettings.columnsConfig = e;
    this.gridSettings.realColWidths = [];
    this.saveGrid();
  }

  /**
   * Get the current column widths and save them.
   */
  public columnResizeEvent($event: any) {
    this.gridSettings.realColWidths = this.vixxoGrid
      .getKendoGridInstance()
      .columns.filter(item => item.isVisible)
      .map(item => item.width);
    this.saveGrid();
  }

  /**
   * Arguments for the dataStateChange event
   * @param state
   */
  public dataStateChange(state: State) {
    this.gridSettings.state = state;
    this.saveGrid();
  }

  /**
   *
   * @param gridSettings custom GridSettings object by using custom logic.
   */
  public mapGridSettings(gridSettings: GridSettings): GridSettings {
    const state = gridSettings.state;
    return {
      state,
      columnsConfig: gridSettings.columnsConfig,
      realColWidths:
        gridSettings.realColWidths && gridSettings.realColWidths.length > 0
          ? gridSettings.realColWidths
          : []
    };
  }

  /**
   * save grid
   */
  private saveGrid(): void {
    const gridConfig: GridSettings = {
      columnsConfig: this.gridSettings.columnsConfig,
      state: this.gridSettings.state,
      realColWidths: this.gridSettings.realColWidths
    };
    this.productService.setStatePersistingInputs(gridConfig);
  }

  /**
   * grid settings
   */
  public gridSettings: GridSettings = {
    state: {
      skip: 0,
      take: 5,
      filter: {
        logic: 'and',
        filters: []
      }
    },
    columnsConfig: this.columns,
    realColWidths: []
  };
}
